import {
    Component,
    OnDestroy,
    OnInit,
    ViewChild,
    HostListener
} from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { ComponentsService } from 'src/app/components.service';
import { EventService } from 'src/app/services/event.service';
import { TimeZoneService } from 'src/app/services/time-zone.service';
import { SearchFilters } from '../ticket-search-panel/ticket-search-panel.component';
import * as moment from 'moment-timezone';
import { TicketDetails } from './models/ticket-details.model';
import { FormControl } from '@angular/forms';
import {
    animate,
    state,
    style,
    transition,
    trigger
} from '@angular/animations';

// Import interfaces and types
interface TicketReportDTO {
    // Add interface definition based on your actual DTO
    [key: string]: any;
}

const SIMULATION_GAME_CODE = 'simulation'; // Define constant

@Component({
    selector: 'app-tiket',
    templateUrl: './tiket.component.html',
    styleUrls: ['./tiket.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition(
                'expanded <=> collapsed',
                animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
            )
        ])
    ]
})
export class TiketComponent implements OnInit, OnDestroy {
    @ViewChild(MatSort) sort: MatSort;
    startNumber: number = 0;
    numberOfRows: number = 100;
    pagingDirection: string = 'f';

    ELEMENT_DATA: any = [];
    ticketNum: number;
    shopNameAndLocation: any;
    saveInProgress: boolean = true;
    postAtnName: any;
    showUserPos: boolean;
    userUuid: any;
    showUserUuid: boolean;
    pageIndex = 0;
    pageSize = 100;
    length = 0; // total from backend
    totalCount = 0;
    currentStatusFilter = '';
    displayedColumns: string[] = [
        'status',
        'ticket_uuid',
        'payin_time',
        'ccy',
        'exchange_rate',
        'payin',
        'payin_base',
        'payout',
        'payout_base',
        'user_uuid'
    ];
    columnMapping: { [key: string]: string } = {
        status: 'isWon',
        ticket_uuid: 'ticketUuid',
        payin_time: 'created',
        payout_time: 'updated',
        ccy: 'ccy',
        exchange_rate: 'ccy_rate',
        payin: 'payin',
        payin_base: 'payin_base',
        payout: 'payout',
        payout_base: 'payout_base',
        user_uuid: 'userUuid'
    };
    statColumns: string[] = ['num', 'payin', 'payout', 'revenue', 'profit'];
    dataSource;
    statisticsSource;
    params;
    clientUuid;
    message: string = '';
    subObserver;
    statisticObserver;
    ticketsObserver;
    paramsSub;
    loading: boolean = false;
    private ngUnsubscribe = new Subject();
    gameCode = SIMULATION_GAME_CODE;
    currentUserUuidFilter: string = '';
    currentTicketUuidFilter: string = '';
    tableModule;
    paginationNumbers;
    filteredTicketsObserver;
    filteredTicketsNumObserver;
    loadingTicketsObserver;
    loadingTicketsNumObserver;
    loadingTicketsStatisticObserver;
    statisticLoaded: boolean = true;
    max_payout;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    toggleControl = new FormControl(false);
    darkMode: boolean;
    eventObserver$: any;
    statisticParamStart: string = '';
    statisticParamEnd: string = '';
    statisticParamDevice: string = 'ALL';
    showLocation: boolean = false;
    sortKey: string = '';
    sortDirection: string = 'asc';
    sortColumn: string = '';
    // Properties for tracking expanded rows and loading states
    expandedRows = new Set<string>();
    loadingDetails = new Set<string>();
    ticketDetailsMap = new Map<string, any>();
    errorStates = new Map<string, string>();
    loadedParams: boolean = false;
    currentTimeZone: string = 'Europe/Belgrade';

    constructor(
        private service: ComponentsService,
        private router: ActivatedRoute,
        private eventService: EventService,
        private timeZoneService: TimeZoneService // Inject TimeZoneService
    ) {
        this.loadingTicketsObserver = this.service.loadingTickets.subscribe(
            (e) => {
                this.message = e.message;
            }
        );
        this.loadingTicketsNumObserver =
            this.service.loadingTicketsNum.subscribe((e) => {
                this.length = e.loaded ? this.length : 0;
            });
        this.loadingTicketsStatisticObserver =
            this.service.loadingTicketsStatistic.subscribe((e) => {
                this.statisticLoaded = e.loaded;
            });

        // Subscribe to time zone changes to update currentTimeZone and refresh data
        this.timeZoneService.selectedTimeZone$.subscribe((tz) => {
            this.currentTimeZone = tz;

            // Refresh data with new timezone-aware date ranges
            if (this.ELEMENT_DATA.length > 0) {
                const defaultDateRange = this.getDefaultDateRange();
                const refreshFilters = {
                    ...defaultDateRange,
                    userUuid: this.currentUserUuidFilter || '',
                    ticketUuid: this.currentTicketUuidFilter || '',
                    status: []
                };

                this.fetchTicketsWithStandardHandling(refreshFilters, {
                    showLoading: true,
                    resetPagination: true
                }).subscribe();
            }
        });
    }

    getMappedSortColumn() {
        return this.columnMapping[this.sortColumn] || this.sortColumn; // Use the mapped name or fallback to the original
    }

    sortChanged(sort: Sort) {
        this.sortColumn = sort.active;
        this.sortDirection = sort.direction;

        // Reset only pagination, keep data, sorting, and filters
        this.clearTableDataAndFilters(false, false, false);

        let filters = this.service.getAllTicketsFilters;
        filters.sortColumn = this.getMappedSortColumn();
        filters.sortDirection = this.sortDirection;

        const initialFilters = {
            pageSize: 100
        };

        this.fetchTicketsWithStandardHandling(initialFilters, {
            resetPagination: true
        }).subscribe();
    }

    ngOnInit(): void {
        this.clientUuid = this.service.getClientUuid();
        this.router.url.subscribe((url) => {
            this.service.setClientUuid(this.service.getUsersPerm().clientUuid);
        });

        this.paramsSub = this.router.params.subscribe((params) => {
            this.params = params;
            this.loadedParams = true;
            this.clientUuid = this.service.getClientUuid();
        });

        this.statisticObserver = this.service.ticketsStatistics.subscribe(
            (data) => {
                this.service.loadingTicketsStatistic.emit({
                    message: 'Statistic loaded',
                    loaded: true
                });
                this.length = data.count;
                this.totalCount = data.count;
                this.statisticsSource = new MatTableDataSource<ticketStatistic>(
                    [
                        {
                            num: data.count,
                            payin: data.totalPayin,
                            profit: data.profit,
                            payout: data.totalPayout,
                            revenue: data.revenue
                        }
                    ]
                );
                this.statisticParamStart =
                    this.service.getAllTicketsFilters.dateFrom;
                this.statisticParamEnd =
                    this.service.getAllTicketsFilters.dateTo;
            }
        );

        this.ticketsObserver = this.service.newTicketBatch.subscribe((data) => {
            this.service.loadingTickets.emit({
                message: 'Tickets loaded',
                loaded: true
            });
            this.ELEMENT_DATA = this.formatTickets(data.tickets);
            if (this.ELEMENT_DATA && this.ELEMENT_DATA[0])
                this.ELEMENT_DATA[0].selected = true;

            // Data is already filtered by backend
            this.dataSource = new MatTableDataSource<tableModuleSIM>(
                this.ELEMENT_DATA
            );

            // Configure custom sorting for numeric columns
            // Important: Lost tickets (isWon === 0) should sort as 0 for payout columns
            this.dataSource.sortingDataAccessor = (item, property) => {
                switch (property) {
                    case 'payout':
                        return item.isWon === 0
                            ? 0
                            : parseFloat(item.payout || '0');
                    case 'payout_base':
                        return item.isWon === 0
                            ? 0
                            : parseFloat(item.payoutBase || '0');
                    case 'payin':
                        return parseFloat(item.payin || '0');
                    case 'payin_base':
                        return parseFloat(item.payinBase || '0');
                    case 'exchange_rate':
                        return parseFloat(item.ccyRate || '0');
                    default:
                        return item[property];
                }
            };

            // Update totalCount when new data arrives
            if (data.totalCount !== undefined) {
                this.totalCount = data.totalCount;
            } else if (data.count !== undefined) {
                this.totalCount = data.count;
            } else if (data.tickets && data.tickets.length) {
                this.totalCount = data.tickets.length;
            }
            // Set sort after dataSource is created and defined
            this.dataSource.sort = this.sort;
        });

        this.eventObserver$ = this.eventService
            .onEvent$()
            .subscribe((event) => {
                if (event && event.type === 'event-theme-change') {
                    this.darkMode = event.payload;
                }
            });

        this.service.selectedLocation.subscribe((data) => {
            this.shopNameAndLocation = data.shop_name + ' ' + data.location_id;
            this.showLocation = data.selected;
        });

        this.service.selectedUser.subscribe((data) => {
            this.postAtnName = data.user;
            this.showUserPos = data.selected;
        });

        this.service.selectedUserUuid.subscribe((data) => {
            this.userUuid = data;
            if (this.userUuid === '' || !this.userUuid) {
                this.showUserUuid = false;
            } else {
                this.showUserUuid = true;
            }
        });

        if (this.service.getUsersPerm().superAdmin == 1) {
            this.subObserver = this.service.clientChanged.subscribe(
                (client) => {
                    console.log(
                        `[TiketComponent] Received clientChanged: ${client.clientUuid}, name: ${client.name}`
                    );
                    // Clear existing data and reset state before switching clients
                    this.clearTableDataAndFilters();
                    this.service.setClientUuid(client.clientUuid);
                    this.service.setSelectedClientUuid(client.clientUuid);
                    console.log(
                        `[TiketComponent] After setting UUIDs: selectedClientUuid=${this.service.getSelectedClientUuid()}`
                    );
                    // Refresh ticket data when client changes
                    this.refreshData();
                }
            );
        } else {
            // For sub-admins and other users, also listen for client changes
            // in case they have client selection capability
            this.subObserver = this.service.clientChanged.subscribe(
                (client) => {
                    console.log(
                        `[TiketComponent] Received clientChanged for sub-admin: ${client.clientUuid}, name: ${client.name}`
                    );
                    // Clear existing data and reset state before switching clients
                    this.clearTableDataAndFilters();
                    this.service.setSelectedClientUuid(client.clientUuid);
                    console.log(
                        `[TiketComponent] After setting UUIDs for sub-admin: selectedClientUuid=${this.service.getSelectedClientUuid()}`
                    );
                    // Refresh ticket data for sub-admins if they can select clients
                    this.refreshData();
                }
            );
        }
    }

    /**
     * Resets table state with configurable options
     * @param clearData - Whether to clear table data and caches (default: true for backward compatibility)
     * @param resetSort - Whether to reset sorting state (default: true for backward compatibility)
     * @param resetFilters - Whether to reset search filters (default: true for backward compatibility)
     */
    private clearTableDataAndFilters(
        clearData: boolean = true,
        resetSort: boolean = true,
        resetFilters: boolean = true
    ): void {
        console.log('[TiketComponent] Resetting table state');

        // Always reset pagination state and paginator indicator
        this.pageIndex = 0;
        this.pageSize = 100;
        this.startNumber = 0;
        this.numberOfRows = 100;
        this.length = 0;
        this.totalCount = 0;
        this.paginator?.firstPage();

        // Clear data and caches if requested
        if (clearData) {
            this.ELEMENT_DATA = [];
            if (this.dataSource) {
                this.dataSource.data = [];
            }
            if (this.statisticsSource) {
                this.statisticsSource.data = [];
            }
            this.ticketDetailsMap.clear();
            this.expandedRows.clear();
            this.loadingDetails.clear();
            this.errorStates.clear();
        }

        // Reset sorting if requested
        if (resetSort) {
            this.sortColumn = '';
            this.sortDirection = '';
        }

        // Reset search filters if requested
        if (resetFilters) {
            this.currentUserUuidFilter = '';
            this.currentTicketUuidFilter = '';
            this.currentStatusFilter = '';
            this.userUuid = '';
            this.showUserUuid = false;
        }

        // Reset loading states and messages
        this.statisticLoaded = false;
        this.loading = false;
        this.message = '';

        console.log('[TiketComponent] Table state reset complete');
    }

    formatTickets(data: any): TicketReportDTO[] {
        this.service.loadingTickets.emit({ message: '', loaded: true });
        this.message = '';
        let tickets = data.map((item) => {
            let ticketDetails: TicketDetails = {} as TicketDetails;
            if (item.ticketDetails && item.ticketDetails !== 'undefined') {
                try {
                    ticketDetails = JSON.parse(item.ticketDetails);
                } catch (e) {
                    console.error(
                        'Invalid JSON in ticketDetails:',
                        item.ticketDetails,
                        e
                    );
                    ticketDetails = {} as TicketDetails;
                }
            }
            const ticketDto: TicketReportDTO = {
                ticketUuid: item.ticketUuid,
                clientUuid: item.clientUuid,
                userUuid: item.userUuid,
                ticketDetails,
                payin: item.payin,
                ccy: item.ccy,
                payinTime: item.created, // Keep as string for UTC parsing
                payoutTime: item.updated, // Keep as string for UTC parsing
                payout: item.payout,
                payinBase: item.payinBase,
                payoutBase: item.payoutBase,
                ccyRate: item.ccyRate,
                isWon: item.isWon
            };
            return ticketDto;
        });

        return tickets;
    }

    pageEvents(e: PageEvent) {
        this.pageIndex = e.pageIndex;
        this.pageSize = e.pageSize;

        let filters = this.service.getAllTicketsFilters;
        if (this.sortColumn && this.sortColumn !== '') {
            filters.sortColumn = this.getMappedSortColumn();
            filters.sortDirection = this.sortDirection;
        }
        const paginationFilters = {
            pageIndex: this.pageIndex,
            pageSize: 100
        };

        this.fetchTicketsWithStandardHandling(paginationFilters).subscribe();
    }

    /**
     * Fetches and processes ticket details for a given row
     * @param row The ticket row to get details for
     */
    getTicketDetailsForRow(row: any) {
        if (!row) {
            console.warn('No row provided to getTicketDetailsForRow');
            return;
        }

        const ticketUuid = row.ticketUuid;

        // If already loading or no ticket details, skip
        if (this.loadingDetails.has(ticketUuid) || !row.ticketDetails) {
            return;
        }

        // Set loading state
        this.loadingDetails.add(ticketUuid);
        this.errorStates.delete(ticketUuid);

        // Parse selections and simulations with better error handling
        let selections = [];
        let simulations = [];

        try {
            // Parse selections
            if (row.ticketDetails.selections) {
                selections =
                    typeof row.ticketDetails.selections === 'string'
                        ? JSON.parse(row.ticketDetails.selections)
                        : row.ticketDetails.selections;

                if (!Array.isArray(selections)) {
                    throw new Error('Selections is not an array');
                }
            }

            // Parse simulations
            if (row.ticketDetails.simulations) {
                simulations =
                    typeof row.ticketDetails.simulations === 'string'
                        ? JSON.parse(row.ticketDetails.simulations)
                        : row.ticketDetails.simulations;

                if (!Array.isArray(simulations)) {
                    throw new Error('Simulations is not an array');
                }
            }
        } catch (e) {
            console.error('Error parsing ticket details:', e);
            this.errorStates.set(ticketUuid, 'Failed to parse ticket details');
            this.loadingDetails.delete(ticketUuid);
            return;
        }

        try {
            // Process selections with simulations
            const processedSelections = selections.map((selection) => {
                const matchingSimulation = simulations.find(
                    (sim) => sim.EID === selection.EID
                );

                // Format the result for display
                let result = null;
                if (matchingSimulation?.result) {
                    try {
                        result =
                            typeof matchingSimulation.result === 'string'
                                ? matchingSimulation.result
                                : JSON.stringify(matchingSimulation.result);
                    } catch (e) {
                        console.warn('Error formatting result:', e);
                        result = 'Result unavailable';
                    }
                }

                return {
                    id:
                        selection.id ||
                        `${selection.EID}-${selection.MC}-${selection.SC}`,
                    sport: selection.S || 'N/A',
                    home: selection.HN || 'N/A',
                    away: selection.AN || 'N/A',
                    market: selection.MN || selection.MC || 'N/A',
                    selection: selection.SN || selection.SC || 'N/A',
                    odd: selection.ODD || 0,
                    isWon: matchingSimulation ? matchingSimulation.isWon : null,
                    result: result
                };
            });

            // Create ticket object with processed data
            const ticketObject = {
                ticketUuid: row.ticketUuid,
                isWon: row.isWon,
                payin: row.payin || 0,
                payout: row.payout || 0,
                currency: row.ccy || 'USD',
                createdAt: row.created || new Date().toISOString(),
                updatedAt: row.updated || new Date().toISOString(),
                selections: processedSelections
            };

            // Store ticket details
            this.ticketDetailsMap.set(ticketUuid, ticketObject);

            // Emit for any parent components that might be listening
            this.service.ticketDetailsEmmiter.emit(ticketObject);
        } catch (e) {
            console.error('Error processing ticket details:', e);
            this.errorStates.set(ticketUuid, 'Error processing ticket details');
        } finally {
            // Clear loading state
            this.loadingDetails.delete(ticketUuid);
        }
    }

    /**
     * Toggles the expansion state of a row
     * @param row The row to toggle
     * @param event Optional click event
     */
    toggleRow(row: any, event?: Event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        const ticketUuid = row.ticketUuid;
        const isExpanded = this.expandedRows.has(ticketUuid);

        if (isExpanded) {
            // Close the dropdown
            this.closeDropdown(ticketUuid);
            return;
        }

        // Allow multiple rows to be expanded - remove this section

        // Open the dropdown
        this.expandedRows.add(ticketUuid);
        this.check(row);

        // Only fetch details if we don't already have them
        if (
            !this.ticketDetailsMap.has(ticketUuid) &&
            !this.loadingDetails.has(ticketUuid)
        ) {
            this.getTicketDetailsForRow(row);
        }
    }

    check(row) {
        this.ELEMENT_DATA.forEach((item) => {
            item.selected = false;
        });
        row.selected = true;
    }

    @HostListener('window:beforeunload')
    ngOnDestroy() {
        if (this.subObserver) {
            this.subObserver.unsubscribe();
        }
        if (this.loadingTicketsObserver) {
            this.loadingTicketsObserver.unsubscribe();
        }
        if (this.loadingTicketsNumObserver) {
            this.loadingTicketsNumObserver.unsubscribe();
        }
        if (this.loadingTicketsStatisticObserver) {
            this.loadingTicketsStatisticObserver.unsubscribe();
        }
        if (this.filteredTicketsObserver) {
            this.filteredTicketsObserver.unsubscribe();
        }
        if (this.filteredTicketsNumObserver) {
            this.filteredTicketsNumObserver.unsubscribe();
        }
        if (this.statisticObserver) {
            this.statisticObserver.unsubscribe();
        }
        if (this.ticketsObserver) {
            this.ticketsObserver.unsubscribe();
        }

        this.paramsSub.unsubscribe();
        this.ngUnsubscribe.next(undefined);
        this.ngUnsubscribe.complete();
        if (this.eventObserver$) {
            this.eventObserver$.unsubscribe();
        }
    }

    /**
     * Closes the dropdown for a specific ticket or all tickets
     * @param ticketUuid Optional ticket UUID to close, or close all if not provided
     */
    closeDropdown(ticketUuid?: string) {
        if (ticketUuid) {
            this.expandedRows.delete(ticketUuid);
            // Don't clear the ticket details from the map to avoid refetching
            // this.ticketDetailsMap.delete(ticketUuid);
            this.errorStates.delete(ticketUuid);
        } else {
            this.expandedRows.clear();
            // Keep the ticket details in the map for better UX when reopening
            // this.ticketDetailsMap.clear();
            this.errorStates.clear();
        }
    }

    /**
     * Checks if a row is currently expanded
     * @param row The row to check
     * @returns boolean indicating if the row is expanded
     */
    isRowExpanded(row: any): boolean {
        return row && row.ticketUuid
            ? this.expandedRows.has(row.ticketUuid)
            : false;
    }

    /**
     * TrackBy function for selection rows to improve performance
     */
    trackBySelection = (index: number, selection: any): string => {
        if (!selection) return `selection-${index}`;
        return (
            selection.id ||
            `${selection.sport}-${selection.market}-${selection.selection}-${selection.home}-${selection.away}`
        );
    };

    /**
     * TrackBy function for ticket rows to improve performance
     */
    trackByTicketId = (index: number, ticket: any): string => {
        return ticket?.ticketUuid || `ticket-${index}`;
    };

    // Method to determine if a row is an expansion detail row
    isExpansionDetailRow = (i: number, row: Object) => {
        return false; // This will be handled by the template logic
    };

    addMaxPayout(maxp) {
        this.max_payout = maxp;
    }

    refreshData() {
        console.log(
            `[TiketComponent] refreshData called, getting client UUID...`
        );
        // Set loading states
        this.statisticLoaded = false; // Directly set the component's loading state
        this.service.loadingTicketsStatistic.emit({
            message: 'Loading statistics...',
            loaded: false
        });
        this.service.loadingTickets.emit({
            message: 'Loading tickets...',
            loaded: false
        });

        // Use standardized fetch method for refresh
        this.fetchTicketsWithStandardHandling(
            {},
            {
                showLoading: true
            }
        ).subscribe({
            next: () => {
                // Emit the refreshPage event for any other components that might be listening
                this.service.refreshPage.emit();
            }
        });
    }

    /**
     * Helper method to get period start date
     * @returns Date object or null
     */
    getPeriodStartDate(): Date | null {
        return this.statisticParamStart
            ? new Date(this.statisticParamStart)
            : null;
    }

    /**
     * Helper method to get period end date
     * @returns Date object or null
     */
    getPeriodEndDate(): Date | null {
        return this.statisticParamEnd ? new Date(this.statisticParamEnd) : null;
    }

    /**
     * Gets the default date range (start of current month to end of today)
     * Used for initial load and clear button functionality
     * @returns Object with dateFrom and dateTo strings
     */
    private getDefaultDateRange(): { dateFrom: string; dateTo: string } {
        const selectedTimezone = this.timeZoneService.getSelectedTimeZone();

        // Get current date in selected timezone
        const nowInTimezone = moment.tz(selectedTimezone);
        const startOfMonthInTimezone = nowInTimezone
            .clone()
            .startOf('month')
            .startOf('day');
        const endOfTodayInTimezone = nowInTimezone.clone().endOf('day');

        return {
            dateFrom: startOfMonthInTimezone
                .utc()
                .format('YYYY-MM-DD HH:mm:ss.SSS'),
            dateTo: endOfTodayInTimezone.utc().format('YYYY-MM-DD HH:mm:ss.SSS')
        };
    }

    /**
     * Centralized method for fetching tickets with standardized handling
     * @param filters Filter object
     * @param options Configuration options for the request
     */
    private fetchTicketsWithStandardHandling(
        filters: any = {},
        options: {
            showLoading?: boolean;
            resetPagination?: boolean;
            emitStatistics?: boolean;
        } = {}
    ): Observable<any> {
        // Set default options
        const opts = {
            showLoading: false,
            resetPagination: false,
            emitStatistics: true,
            ...options
        };

        // Standardize filter preparation
        const standardFilters = this.prepareStandardFilters(
            filters,
            opts.resetPagination
        );

        // Set loading states if requested
        if (opts.showLoading) {
            this.setLoadingStates(true);
        }

        const clientUuid = this.service.getSelectedClientUuid();
        console.log(
            `[TiketComponent] fetchTicketsWithStandardHandling - fetching for client: ${clientUuid}`,
            { filters: standardFilters, options: opts }
        );

        return this.service
            .getAllTicketsForClient(clientUuid, standardFilters)
            .pipe(
                finalize(() => {
                    if (opts.showLoading) {
                        this.setLoadingStates(false);
                    }
                }),
                tap((data) => {
                    // Calculate statistics from filtered ticket data
                    if (opts.emitStatistics && data) {
                        const totalPayin = data.totalPayin || 0;
                        const totalPayout = data.totalPayout || 0;
                        const revenue = totalPayin - totalPayout;
                        const profit =
                            totalPayin > 0 ? (revenue * 100) / totalPayin : 0;

                        // Update statistics with filtered data
                        this.statisticsSource =
                            new MatTableDataSource<ticketStatistic>([
                                {
                                    num: data.count || 0,
                                    payin: totalPayin,
                                    profit: profit, // Calculate percentage like Super Admin
                                    payout: totalPayout,
                                    revenue: revenue
                                }
                            ]);

                        // Emit statistics events
                        this.service.ticketsStatistics.emit({
                            count: data.count,
                            totalPayin: totalPayin,
                            totalPayout: totalPayout,
                            revenue: revenue,
                            profit: profit
                        });

                        this.service.loadingTicketsStatistic.emit({
                            loaded: true,
                            message: 'Statistics loaded'
                        });
                    }

                    // Process ticket data - emit newTicketBatch to populate table
                    this.service.newTicketBatch.emit(data);
                    this.service.loadingTickets.emit({
                        message: 'Tickets loaded',
                        loaded: true
                    });
                }),
                catchError((error) => {
                    console.error('Error fetching tickets:', error);
                    this.handleTicketError(error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Prepares standardized filters for API calls
     */
    private prepareStandardFilters(
        filters: any,
        resetPagination: boolean = false
    ): any {
        let serviceFilters = {
            ...this.service.getAllTicketsFilters,
            ...filters
        };

        // Set pagination defaults
        if (resetPagination) {
            serviceFilters.pageIndex = 0;
        }
        if (!serviceFilters.pageSize) {
            serviceFilters.pageSize = 100;
        }
        if (serviceFilters.pageIndex === undefined) {
            serviceFilters.pageIndex = 0;
        }

        // Apply sorting if set
        if (this.sortColumn) {
            serviceFilters.sortColumn = this.getMappedSortColumn();
            serviceFilters.sortDirection = this.sortDirection;
        }

        return serviceFilters;
    }

    /**
     * Sets loading states consistently
     */
    private setLoadingStates(loading: boolean): void {
        this.loading = loading;
        this.statisticLoaded = !loading;

        const message = loading ? 'Loading...' : 'Loading completed';
        this.service.loadingTickets.emit({ message, loaded: !loading });
        this.service.loadingTicketsStatistic.emit({
            message,
            loaded: !loading
        });
    }

    /**
     * Processes ticket data consistently
     */
    private processTicketData(data: any, emitStatistics: boolean = true): void {
        // Update statistics if data contains them
        if (data.count !== undefined) {
            this.length = data.count;
            this.statisticsSource = new MatTableDataSource<ticketStatistic>([
                {
                    num: data.count,
                    payin: data.totalPayin,
                    profit: data.profit,
                    payout: data.totalPayout,
                    revenue: data.revenue
                }
            ]);
            this.statisticParamStart =
                this.service.getAllTicketsFilters?.dateFrom;
            this.statisticParamEnd = this.service.getAllTicketsFilters?.dateTo;
        }

        // Emit events if requested
        if (emitStatistics) {
            this.service.ticketsStatistics.emit(data);
            this.service.newTicketBatch.emit(data);
        }

        // Clear any error messages
        this.message = '';
    }

    /**
     * Handles ticket fetch errors consistently
     */
    private handleTicketError(error: any): void {
        this.loading = false;
        this.statisticLoaded = true;
        this.message = 'Greška prilikom učitavanja tiketa';

        this.service.loadingTickets.emit({
            message: 'Error loading tickets',
            loaded: true
        });
        this.service.loadingTicketsStatistic.emit({
            message: 'Error loading statistics',
            loaded: true
        });
    }

    /**
     * Prepares search-specific filters with timezone-aware date handling
     */
    private prepareSearchFilters(filters: SearchFilters): any {
        const selectedTimezone = this.timeZoneService.getSelectedTimeZone();

        let serviceFilters: any = {
            userUuid: filters.userUuid || '',
            ticketUuid: filters.ticketUuid || '',
            status: filters.status ? [filters.status] : [] // Send status filter to backend
        };

        // Apply date filters - use provided dates or default to current month
        if (filters.dateFrom) {
            // Convert user-selected date to start of day in selected timezone, then to UTC
            const startOfDayInTimezone = moment
                .tz(filters.dateFrom, selectedTimezone)
                .startOf('day');
            serviceFilters.dateFrom = startOfDayInTimezone
                .utc()
                .format('YYYY-MM-DD HH:mm:ss.SSS');
        } else {
            // Default to start of current month in selected timezone
            const startOfMonthInTimezone = moment
                .tz(selectedTimezone)
                .startOf('month')
                .startOf('day');
            serviceFilters.dateFrom = startOfMonthInTimezone
                .utc()
                .format('YYYY-MM-DD HH:mm:ss.SSS');
        }

        if (filters.dateTo) {
            // Convert user-selected date to end of day in selected timezone, then to UTC
            const endOfDayInTimezone = moment
                .tz(filters.dateTo, selectedTimezone)
                .endOf('day');
            serviceFilters.dateTo = endOfDayInTimezone
                .utc()
                .format('YYYY-MM-DD HH:mm:ss.SSS');
        } else {
            // Default to end of today in selected timezone
            const endOfTodayInTimezone = moment
                .tz(selectedTimezone)
                .endOf('day');
            serviceFilters.dateTo = endOfTodayInTimezone
                .utc()
                .format('YYYY-MM-DD HH:mm:ss.SSS');
        }

        return serviceFilters;
    }

    /**
     * Handles search filters from ticket-search-panel
     * @param filters Search filters object
     */
    onSearchFilters(filters: SearchFilters): void {
        console.log('Search filters applied:', filters);

        // Reset only pagination, keep data and sorting, don't reset filters (we're setting new ones)
        this.clearTableDataAndFilters(false, false, false);

        // Store UUID filters for client-side filtering (don't send to server)
        this.currentUserUuidFilter = filters.userUuid?.trim() || '';
        this.currentTicketUuidFilter = filters.ticketUuid?.trim() || '';
        this.currentStatusFilter = filters.status || '';

        // Update component state for user UUID display
        this.userUuid = this.currentUserUuidFilter;
        this.showUserUuid = !!this.currentUserUuidFilter;

        // Prepare search-specific filters
        const searchFilters = this.prepareSearchFilters(filters);

        // Use standardized fetch method
        this.fetchTicketsWithStandardHandling(searchFilters, {
            showLoading: true,
            resetPagination: true
        }).subscribe({
            next: () => {
                // Check if no results found
                if (this.ELEMENT_DATA.length === 0) {
                    this.message =
                        'No tickets found matching the search criteria.';
                }
            },
            error: () => {
                this.message = 'Greška prilikom pretrage tiketa';
            }
        });
    }

    /**
     * Handles clear filters from ticket-search-panel
     */
    onClearFilters(): void {
        console.log('Filters cleared');

        // Reset pagination and filters, keep data and sorting
        this.clearTableDataAndFilters(false, false, true);

        // Use the same default date range as initial load (start of month to today)
        const defaultDateRange = this.getDefaultDateRange();
        const defaultFilters = {
            ...defaultDateRange,
            userUuid: '',
            ticketUuid: '',
            status: []
        };

        // Use standardized fetch method
        this.fetchTicketsWithStandardHandling(defaultFilters, {
            showLoading: true,
            resetPagination: true
        }).subscribe();
    }

    onPageChange(event: PageEvent): void {
        this.pageIndex = event.pageIndex;
        this.pageSize = event.pageSize;
        this.numberOfRows = event.pageSize;
        this.startNumber = event.pageIndex * event.pageSize;

        // Use standardized fetch method for pagination
        const paginationFilters = {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            startNumber: this.startNumber,
            numberOfRows: this.numberOfRows
        };

        this.fetchTicketsWithStandardHandling(paginationFilters, {
            showLoading: true
        }).subscribe();
    }
}

export interface tableModuleSIM {
    ticket_uuid: string;
    payin_time: string;
    ccy: string;
    exchange_rate: number;
    payin: number;
    payin_base: number;
    payout: number;
    payout_base: number;
    user_uuid: string;
}

export interface ticketStatistic {
    num: number;
    payin: number;
    payout: number;
    revenue: number;
    profit: number;
}
